import {
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  Grid,
  Group,
  Popover,
  SegmentedControl,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconCopy,
  IconDeviceFloppy,
  IconExternalLink,
  IconInfoCircle,
  IconLock,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { handleBarsParams as allHandleBarsParams } from '../../constants/HandleBarsParams';
import templateWizardTemplates from '../../constants/templateWizardTemplates';
import transformWizardStyles from '../../helpers/transformWizardStyles';
import BadgeWithIcon from '../BadgeWithIcon';
import HandlebarsHtmlEditor from '../Editors/HandlebarsHtmlEditor';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import Spinner from '../Spinner';
import EditOnMySite from './EditOnMySite';
import WizardForm from './ProductBlocksFormElements/WizardForm';
import TemplatePreview from './TemplatePreview';

const TemplateForm = ({ template: passedTemplate, deleteTemplate, saveTemplate, cancel }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId, domainId } = useParams();
  const [template, setTemplate] = useState(passedTemplate);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmChangeOpen, setConfirmChangeOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(!passedTemplate?.id);
  const [pendingSave, setPendingSave] = useState(null);
  const accountName = useSelector((state) => state.account.account)?.name;
  const domainName = useSelector((state) => state.domain.domain)?.name;

  const isReadOnly = !isEditing;

  // Shopify accounts are often named after their myshopify domain; showing
  // "x.myshopify.com / x.myshopify.com" is noise, so collapse identical names.
  // Drop any falsy value so the banner never renders the string "undefined"
  // if account/domain state hasn't loaded yet.
  const editingLocation = [...new Set([accountName, domainName].filter(Boolean))].join(' / ');

  const handleChange = (e) => {
    if (isReadOnly) {
      return;
    }

    const { name, value } = e.target;

    setTemplate((prevTemplate) => {
      let updatedTemplate = JSON.parse(JSON.stringify(prevTemplate));

      if (name === 'scope') {
        if (!prevTemplate.createdAt && prevTemplate.body === '') {
          const body = templateWizardTemplates[value];
          updatedTemplate = { ...updatedTemplate, [name]: value, body };
        } else {
          updatedTemplate = { ...updatedTemplate, [name]: value };
        }

        if (value === 'search') {
          updatedTemplate.advancedEditor = true;
        }
      } else {
        updatedTemplate = { ...updatedTemplate, [name]: value };
      }

      return updatedTemplate;
    });
  };

  if (!template) {
    return <Spinner />;
  }

  const saveDisabled = !template.name || !template.type || !template.scope || !template.body;

  const recommenderCount = template.recommenders?.length || 0;
  const emailCount = template.emails?.length || 0;
  const isLiveTemplate = Boolean(template.id) && recommenderCount + emailCount > 0;

  const usageParts = [];
  if (recommenderCount) {
    usageParts.push(`${recommenderCount} recommender${recommenderCount === 1 ? '' : 's'}`);
  }
  if (emailCount) {
    usageParts.push(`${emailCount} email${emailCount === 1 ? '' : 's'}`);
  }

  const requestSave = (redirect) => {
    if (isLiveTemplate) {
      setPendingSave({ redirect });
    } else {
      saveTemplate(template, redirect);
    }
  };

  const createEmailBadge = (email, index) => (
    <BadgeWithIcon
      key={index}
      href={`#/accounts/${accountId}/domains/${domainId}/emails/${email.id}/details`}
      title={email.name}
      icon="mail"
    />
  );

  const createWebBadge = (page, recommenders, index) => (
    <div key={index}>
      <BadgeWithIcon
        href={`#/accounts/${accountId}/domains/${domainId}/pages?page=${page.id}&type=recommenders`}
        title={page.name}
        icon="file"
      />
      {recommenders.map((recommender, subIndex) => (
        <BadgeWithIcon
          key={subIndex}
          href={`#/accounts/${accountId}/domains/${domainId}/pages/${recommender.pageId}/recommenders/${recommender.id}/details`}
          title={recommender.name}
          icon="award"
        />
      ))}
    </div>
  );

  const getBadgesByScope = (template) => {
    switch (template.scope) {
      case 'email':
        return template.emails?.map(createEmailBadge);
      case 'web': {
        const pagePerRecommenders = template.recommenders.reduce((accu, recommender) => {
          const { pageId, page } = recommender;
          accu[pageId] = accu[pageId] || { page, recommenders: [] };
          accu[pageId].recommenders.push(recommender);
          return accu;
        }, {});
        return Object.values(pagePerRecommenders).map((data, index) =>
          createWebBadge(data.page, data.recommenders, index)
        );
      }
      default:
        return [];
    }
  };

  const badges = getBadgesByScope(template, accountId, domainId);

  const showDeleteBtn = !(template.recommenders?.length || template.emails?.length);

  const popover = (
    <Popover>
      <Grid.Col span={12}>
        <span style={{ color: 'var(--mantine-color-red-6)' }}>
          {'Delete is disabled since the template is used here:'}
        </span>
        <br />
        {badges}
      </Grid.Col>
    </Popover>
  );

  const deleteButton =
    deleteTemplate && showDeleteBtn ? (
      <LockableButton restrictionKey="layoutsDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    ) : (
      <Tooltip label={popover} position="top">
        <Box display="inline-block">
          <LockableButton restrictionKey="layoutsDelete" color="red" disabled style={{ pointerEvents: 'none' }}>
            <IconTrash size={14} /> Delete
          </LockableButton>
        </Box>
      </Tooltip>
    );

  const duplicateButton = template.id && template.mode !== 'custom' && (
    <LockableButton
      restrictionKey="layoutsCreate"
      color="cyan"
      onClick={() => {
        navigate(location.pathname.replace(`/templates/${template.id}/details`, `/templates/new?from=${template.id}`));
      }}
    >
      <IconCopy size={14} /> Make a copy
    </LockableButton>
  );

  // A hand-written layout that renders on the customer's site is not previewed
  // in the admin: an iframe here has none of the shop's css, so the site itself
  // is where it gets looked at — which is what EditOnMySite opens. That covers
  // web layouts in the HTML editor, and every search layout, since the scope
  // forces the HTML editor on and the dropdown is appended to the search input's
  // own parent — inside the shop's header markup, inheriting from it and matched
  // by its rules, with no isolation anywhere in the SDK.
  //
  // The wizard keeps its preview: it styles what it controls with inline
  // attributes, which beat any theme rule that is not !important, so it renders
  // the same either way. Email keeps its preview too — there is no site to open.
  const previewableOnSite = template.scope === 'web' || template.scope === 'search';
  const showPreview = !(previewableOnSite && template.advancedEditor);
  // Web and search layouts are as wide as a shop page, so the preview takes the
  // whole row and the markup sits underneath it. Email layouts are narrow enough
  // to preview next to their markup.
  const previewOnTop = showPreview && template.advancedEditor && template.scope !== 'email';
  const previewSpan = previewOnTop ? 12 : { base: 12, md: template.advancedEditor ? 5 : 8 };
  // With no preview beside it, the markup editor takes the whole row rather than
  // leaving the freed columns empty.
  const editorSpan = previewOnTop || !showPreview ? 12 : { base: 12, md: 7 };

  const handleBarsParams =
    template.scope === 'web'
      ? allHandleBarsParams
      : allHandleBarsParams.filter((param) => param.name !== '{{find custom.string "key" "categoryLabelHtml"}}');

  const handleBarsParamsItems = handleBarsParams.map((param) => {
    return (
      <Table.Tr key={param.name}>
        <Table.Td width={300}>{param.name}</Table.Td>
        <Table.Td>{param.description}</Table.Td>
      </Table.Tr>
    );
  });

  return (
    <>
      <Grid.Col span={12} ta="left">
        {isEditing && Boolean(template.id) && (
          <Alert color="orange" icon={<IconPencil size={18} />} mb="md">
            You are editing <strong>{template.name}</strong> in <strong>{editingLocation}</strong>
          </Alert>
        )}
        {isReadOnly && (
          <Alert color="blue" icon={<IconLock size={18} />} mb="md">
            This layout is read-only. Click Edit to make changes.
          </Alert>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Text size="sm" fw={500}>
                Name
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <TextInput
                name="name"
                type="text"
                value={template.name}
                placeholder="e.g. Slider Template"
                disabled={isReadOnly}
                onChange={(e) => handleChange(e)}
              />
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }}>Scope</Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <SegmentedControl
                disabled={!!(template.id || template.scope || isReadOnly)}
                value={template.scope ?? ''}
                onChange={(value) => handleChange({ target: { name: 'scope', value } })}
                data={[
                  { label: 'Web', value: 'web' },
                  { label: 'Email', value: 'email' },
                  { label: 'Search As You Type', value: 'search' },
                ]}
              />
            </Grid.Col>
          </Grid>
          {template.scope && (
            <>
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }}>Editor</Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SegmentedControl
                    disabled={template.advancedEditor === true || isReadOnly}
                    value={String(template.advancedEditor)}
                    onChange={() => {
                      if (!isReadOnly) {
                        setConfirmChangeOpen(true);
                      }
                    }}
                    data={[
                      { label: 'Visual', value: 'false' },
                      { label: 'HTML', value: 'true' },
                    ]}
                  />
                </Grid.Col>
              </Grid>
              <hr />
              <Box display={template.advancedEditor ? 'block' : 'none'} ta="right">
                <Text size="sm" c="dimmed">
                  This layout uses the{' '}
                  <a href="https://handlebarsjs.com/api-reference/" target="_lank" rel="noopener">
                    handlebars templating language <IconExternalLink size={14} />
                  </a>
                  .
                </Text>
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 4 }}></Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <Text size="sm" c="dimmed" component="div">
                      <Card withBorder>
                        <Card.Section ta="left" bg="white" inheritPadding py="sm">
                          <Grid>
                            <Grid.Col span={12} ta="right" p={0}>
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => setOpen(!open)}
                                aria-controls="example-collapse-text"
                                aria-expanded={open}
                              >
                                <IconInfoCircle size={14} /> Custom Helper Functions
                              </Button>
                            </Grid.Col>
                          </Grid>
                        </Card.Section>
                        <Collapse expanded={open}>
                          <Card.Section inheritPadding py="sm">
                            <Grid.Col span={12}>
                              <Table striped withTableBorder highlightOnHover ta="left" style={{ padding: 0 }}>
                                <Table.Tbody>{handleBarsParamsItems}</Table.Tbody>
                              </Table>
                            </Grid.Col>
                          </Card.Section>
                        </Collapse>
                      </Card>
                    </Text>
                  </Grid.Col>
                </Grid>
              </Box>
              <EditOnMySite template={template} />
              <Grid mb="md">
                {!template.advancedEditor && (
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <WizardForm
                      data={template.wizardFormData}
                      disabled={isReadOnly}
                      setData={(wizardFormData) => {
                        const updatedTemplate = { ...template, wizardFormData };
                        setTemplate({ ...updatedTemplate, body: transformWizardStyles(updatedTemplate) });
                      }}
                      template={template}
                    />
                  </Grid.Col>
                )}
                {showPreview && (
                  <Grid.Col span={previewSpan}>
                    <TemplatePreview template={template} />
                  </Grid.Col>
                )}
                {template.advancedEditor ? (
                  <Grid.Col span={editorSpan}>
                    <HandlebarsHtmlEditor
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange({ target: { name: 'body', value } })}
                      name="html"
                      height="500px"
                      value={template.body}
                      readOnly={isReadOnly}
                    />
                  </Grid.Col>
                ) : null}
              </Grid>

              <Group justify="flex-end" gap="xs" py="md">
                <Button variant="default" onClick={cancel}>
                  {' '}
                  Back
                </Button>
                {isReadOnly ? (
                  <>
                    {duplicateButton}
                    <LockableButton restrictionKey="layoutsUpdate" onClick={() => setIsEditing(true)}>
                      <IconPencil size={14} /> Edit
                    </LockableButton>
                  </>
                ) : (
                  <>
                    {deleteButton}
                    {duplicateButton}
                    {template.id ? (
                      <LockableButton
                        color="cyan"
                        onClick={() => requestSave(false)}
                        restrictionKey="layoutsUpdate"
                        disabled={saveDisabled}
                      >
                        <IconDeviceFloppy size={14} /> Save
                      </LockableButton>
                    ) : null}
                    <LockableButton
                      restrictionKey={`layouts${template.id ? 'Update' : 'Create'}`}
                      onClick={() => requestSave(true)}
                      disabled={saveDisabled}
                    >
                      <IconDeviceFloppy size={14} /> Save & close
                    </LockableButton>
                  </>
                )}
                <ConfirmationModal
                  show={confirmDeleteOpen}
                  close={() => setConfirmDeleteOpen(false)}
                  confirm={(cb) => deleteTemplate(cb)}
                  message="This action cannot be undone!"
                />
                <ConfirmationModal
                  show={confirmChangeOpen}
                  close={() => setConfirmChangeOpen(false)}
                  confirm={() => {
                    setConfirmChangeOpen(false);
                    handleChange({ target: { name: 'advancedEditor', value: true } });
                  }}
                  message="Setting Layout Form to Advanced action cannot be undone!"
                />
                <ConfirmationModal
                  show={pendingSave !== null}
                  close={() => setPendingSave(null)}
                  confirm={(closeModal) => {
                    saveTemplate(template, pendingSave.redirect);
                    closeModal();
                  }}
                  message={
                    <>
                      You are saving changes to "{template.name}" in <strong>{editingLocation}</strong>. This layout is
                      live: it is used by {usageParts.join(' and ')}. Please make sure this is the right account and
                      domain.
                    </>
                  }
                />
              </Group>
            </>
          )}
        </form>
      </Grid.Col>
    </>
  );
};

export default TemplateForm;
